/**
 * eSalon Consultation — Analyzing / Results Ready (DEV-4285, step 3)
 *
 * Holds the "Analyzing…" state for a configured duration, then swaps to
 * "Results Ready" and enables the forward controls.
 *
 * The pause is theatrical: the recommendation is a local mapping and resolves
 * immediately, so nothing is actually being awaited here. Reloading replays it,
 * by decision on DEV-4285 — so no "already seen" flag is persisted.
 */

class ConsultationAnalyzing extends HTMLElement {
  connectedCallback() {
    this.profile = this.dataset.profile || 'consultation';

    // Reached without finishing step 1? Leave now, before the analysing theatre
    // starts — there is nothing to analyse, and the step after this one would
    // present an empty profile as a personalised result.
    const gate = window.esalonConsultation || {};
    if (gate.requireComplete && gate.requireComplete(this.profile, this.dataset.requireUrl)) return;

    this.nextUrl = this.dataset.nextUrl || '';
    this.backUrl = this.dataset.backUrl || '';
    this.duration = Number.parseInt(this.dataset.duration, 10);
    if (!Number.isFinite(this.duration) || this.duration < 0) this.duration = 2500;

    this.fade = Number.parseInt(this.dataset.fade, 10);
    if (!Number.isFinite(this.fade) || this.fade < 0) this.fade = 400;

    // One flag for both the artificial wait and the crossfade — neither is doing
    // real work, so someone who has asked for less motion gets the end state.
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.workingState = this.querySelector('[data-analyzing-state="working"]');
    this.readyState = this.querySelector('[data-analyzing-state="ready"]');
    this.announce = this.querySelector('[data-analyzing-announce]');
    this.cta = this.querySelector('[data-analyzing-cta]');
    this.nextArrow = this.querySelector('[data-consultation-next]');
    this.prevArrow = this.querySelector('[data-consultation-prev]');
    this.stepText = this.querySelector('[data-consultation-step-text]');

    this.readyStepLabel = this.dataset.readyStepLabel || '';

    this.bindEvents();
    this.startAnalyzing();
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }

  bindEvents() {
    if (this.cta) {
      this.cta.addEventListener('click', () => {
        if (!this.cta.disabled) this.goNext();
      });
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        if (!this.nextArrow.disabled) this.goNext();
      });
    }

    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        if (this.backUrl) {
          this.navigate(this.backUrl);
        } else {
          window.history.back();
        }
      });
    }
  }

  /**
   * Runs the recommender registered for this profile, if one is loaded.
   *
   * Looked up by profile name rather than imported, so this section stays
   * generic — Custom Care and Custom Color register their own under their own
   * key and need no change here. Returns null when none is registered.
   */
  getRecommendation(answers) {
    const recommender = (window.esalonRecommenders || {})[this.profile];
    if (!recommender) return null;

    try {
      const keys = recommender.recommend(answers);
      return { keys, handles: recommender.toHandles(keys) };
    } catch (error) {
      // A broken rule set must not strand someone on the analyzing screen — the
      // next step can re-derive from the answers, which are persisted anyway.
      console.warn('[Consultation] recommendation failed', error);
      return null;
    }
  }

  /** Answers from the consultation step, if the visitor came through it. */
  getAnswers() {
    try {
      return JSON.parse(sessionStorage.getItem(`esalon:consultation:${this.profile}`) || '{}');
    } catch {
      return {};
    }
  }

  startAnalyzing() {
    // Holding someone on a spinner they didn't ask for is the same class of
    // problem as an animation they didn't ask for, and no real work is pending.
    if (this.duration === 0 || this.reduceMotion) {
      this.showReady();
      return;
    }

    this.timer = window.setTimeout(() => this.showReady(), this.duration);
  }

  wait(ms) {
    return new Promise((resolve) => {
      this.timer = window.setTimeout(resolve, ms);
    });
  }

  /**
   * Fades the analyzing copy and the button out, swaps them while invisible,
   * then fades the ready copy and button back in.
   *
   * Sequential rather than a true crossfade: the two states share a grid cell,
   * so overlapping them would show both sets of text on top of each other.
   */
  async showReady() {
    const fade = this.reduceMotion ? 0 : this.fade;

    // Only fade the step label if it actually changes — fading identical text
    // out and back in just looks like a glitch.
    const swapsStepLabel = Boolean(this.stepText && this.readyStepLabel);

    if (fade > 0) {
      if (this.workingState) this.workingState.setAttribute('data-state-hidden', '');
      if (this.cta) this.cta.setAttribute('data-fading', '');
      if (swapsStepLabel) this.stepText.setAttribute('data-fading', '');
      await this.wait(fade);
    } else if (this.workingState) {
      this.workingState.setAttribute('data-state-hidden', '');
    }

    // Swapped while nothing is visible, so the label change is never seen.
    if (this.readyState) this.readyState.removeAttribute('data-state-hidden');

    if (swapsStepLabel) {
      this.stepText.textContent = this.readyStepLabel;
      this.stepText.removeAttribute('data-fading');
    }

    if (this.cta) {
      this.cta.disabled = false;
      const readyLabel = this.cta.dataset.readyLabel;
      if (readyLabel) this.cta.textContent = readyLabel;
      this.cta.removeAttribute('data-fading');
    }

    if (this.nextArrow) {
      this.nextArrow.disabled = false;
      this.nextArrow.setAttribute('aria-disabled', 'false');
    }

    if (this.announce) this.announce.textContent = 'Your results are ready.';

    // Resolved here rather than on the next page so the routine step can render
    // server-side from a known result, and so a rules failure surfaces while the
    // customer is still on a screen that can recover.
    const answers = this.getAnswers();
    const recommendation = this.getRecommendation(answers);
    if (recommendation) this.persistRecommendation(recommendation);

    this.dispatchEvent(
      new CustomEvent('consultation:results-ready', {
        bubbles: true,
        detail: { profile: this.profile, answers, recommendation },
      })
    );
  }

  /** Hands the result to the routine step, which reads it on load. */
  persistRecommendation(recommendation) {
    try {
      sessionStorage.setItem(
        `esalon:recommendation:${this.profile}`,
        JSON.stringify(recommendation)
      );
    } catch {
      // Private browsing or a full quota. The routine step can re-derive from
      // the answers, so this is recoverable rather than fatal.
    }
  }

  goNext() {
    if (this.nextUrl) this.navigate(this.nextUrl);
  }

  /** The one place this element leaves the page — matches consultation-routine. */
  navigate(url) {
    window.location.assign(url);
  }
}

if (!customElements.get('consultation-analyzing')) {
  customElements.define('consultation-analyzing', ConsultationAnalyzing);
}
